import MenuList from '@components/common/menu/MenuList';
import { buildContextMenuFromType } from '@components/map/contextMenu/builderFactory';
import { HasEventContext, HasMeasureContext, HasQrCodeContext } from '@components/map/contextMenu/builders/common';
import { PoiContextMenuContext } from '@components/map/contextMenu/builders/poi';
import { FeatureTypeToContextComponentEnum } from '@components/map/contextMenu/type';
import { useMapContext } from '@components/map/MapContext';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { MeasureOriginEnum, useMeasureFromPointMenuItem } from '@hooks/useMeasureFromPointMenuItem';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasRole } from '@redux/authent/authent.selectors';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectPoiViewMode, selectPopupsByMapId } from '@redux/maps/maps.selectors';
import { useUpdatePointsOfInterestMutation } from '@services/config/pointOfInterest.api';
import { isOpenedPopupByType } from '@utils/popup.utils';
import { toLngLat } from '@utils/validation/coordinates.utils';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { PointOfInterest } from '@/types/config/pointOfInterest.types';
import { MenuItems } from '@/types/menu.types';
import { PopupDataType, PopupTypeEnum } from '@/types/popup.types';

interface PoiContextMenuProps {
  poi: PointOfInterest;
  clickPosition: { x: number; y: number };
  onClose: () => void;
}

function PoiContextMenu({ poi, onClose, clickPosition }: Readonly<PoiContextMenuProps>) {
  const { formatMessage } = useIntl();
  const { mapId } = useMapContext();
  const viewMode = useAppSelector((state) => selectPoiViewMode(state, mapId));
  const [updatePoi] = useUpdatePointsOfInterestMutation();
  const popups = useSelectorWithMapId(selectPopupsByMapId);
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const accessEventCommand = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_REPLAY));

  if (!viewMode) {
    onClose();
  }

  const handleActivatePoi = () => {
    updatePoi({ id: poi.id, poi: { ...poi, active: !poi.active } })
      .unwrap()
      .then(() =>
        createToast(
          formatMessage({ id: 'poi.toastMessage' }, { code: poi.code, active: poi.active, success: true }),
          ToastStatusEnum.SUCCESS,
        ),
      )
      .catch(() =>
        createToast(
          formatMessage({ id: 'poi.toastMessage' }, { code: poi.code, active: poi.active, success: false }),
          ToastStatusEnum.ERROR,
        ),
      );
    onClose();
  };

  const handleCreateEventClick = useCallback(() => {
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.EVENT,
        position: { x: clickPosition.x, y: clickPosition.y },
        data: {
          newEvent: {
            coordinates: poi.position,
            date: new Date().toISOString(),
          },
        },
      },
    });
    onClose();
  }, [clickPosition.x, clickPosition.y, mapId, onClose, poi.position, updatePopup]);

  const openPopup = (popupDataType: PopupDataType) =>
    updatePopup({
      mapId,
      popup: {
        ...popupDataType,
        position: { x: clickPosition.x, y: clickPosition.y },
      },
    });

  const openGpsQrCodePopup = () => {
    openPopup({
      type: PopupTypeEnum.GPS_QR_CODE,
      data: {
        coordinates: poi.position,
      },
    });
    onClose();
  };

  const [label, enableMeasure] = useMeasureFromPointMenuItem({
    closeContextMenu: onClose,
    position: poi.position,
    measureOrigin: MeasureOriginEnum.CAMERA,
  });

  const measureContext: HasMeasureContext = {
    position: toLngLat(poi.position),
    label,
    enableMeasure,
  };
  const eventContext: HasEventContext = {
    popups,
    accessEventCommand,
    isOpenedPopupByType,
    handleCreateEventClick,
  };
  const qrCodeContext: HasQrCodeContext = {
    position: toLngLat(poi.position),
    openGpsQrCodePopup,
  };

  const ctx: PoiContextMenuContext = {
    isPoiActive: poi.active,
    handleActivatePoi,
    ...eventContext,
    ...measureContext,
    ...qrCodeContext,
  };

  const menuItems: MenuItems = buildContextMenuFromType<FeatureTypeToContextComponentEnum.POI>(
    FeatureTypeToContextComponentEnum.POI,
    mapId,
    ctx,
  );

  return <MenuList menuItems={menuItems} />;
}

export default PoiContextMenu;
